// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the Real time list's rules, shared by the browser's Layers card
 * (mapLayersUI) and the headset's Layers page (vrPanels), so both list
 * the same streams the same way (owner, 2026-11-04: "make sure the
 * filtering logic for real time is the same as for the web version").
 *
 *   - Which streams are listed (`rtListed`): those with dates in common
 *     with the dataset, those whose dates are not known yet or that have
 *     none, and any already playing; the rest are counted for the
 *     "N more streams…" note.
 *   - In what order (`rtListOrder`): topic by topic (the most specific
 *     topic's rank), then A–Z.
 *   - The overlap bar (`rtOverlapBar`): the dataset's dates and the
 *     stream's on one scale, so a glance shows how much they share.
 *   - The dates as words (`rtRangeText`).
 */

import { formatDate } from '../i18n/format'

/** A span of dates, ms since epoch, end exclusive. */
export interface TimeRange {
  readonly startMs: number
  readonly endMs: number
}

/** AYNI: whether two spans share any date. */
export function rangesOverlap(a: TimeRange, b: TimeRange): boolean {
  return a.startMs < b.endMs && b.startMs < a.endMs
}

/** What the rules read of a stream. `range` undefined: its dates are still coming; null: it has none. */
export interface RtListItem {
  readonly id: string
  readonly title: string
  readonly topicRank?: number
  readonly range?: TimeRange | null
}

/** The streams to list and how many were left out for having no dates in common with the dataset. */
export function rtListed<T extends RtListItem>(
  choices: readonly T[],
  base: TimeRange | null,
  playing: readonly string[],
): { listed: T[]; hidden: number } {
  if (!base) return { listed: [...choices], hidden: 0 }
  const listed = choices.filter((c) => !c.range || rangesOverlap(c.range, base) || playing.includes(c.id))
  return { listed, hidden: choices.length - listed.length }
}

/** The browser's order without its group headings: topic by topic (no topic last), then A–Z. */
export function rtListOrder<T extends RtListItem>(choices: readonly T[]): T[] {
  const rank = (c: T) => c.topicRank ?? Number.MAX_SAFE_INTEGER
  return [...choices].sort((a, b) => rank(a) - rank(b) || a.title.localeCompare(b.title))
}

/** A span on the bar, as fractions of its width (0–1). */
export interface BarSpan {
  readonly start: number
  readonly width: number
}

/** The stream's span is drawn at least this wide (a fraction of the bar), so a short stream stays visible. */
const MIN_OWN_WIDTH = 0.03

/**
 * The overlap bar's two spans on one scale running from the earlier start
 * to the later end: the dataset's (outlined green in both places) and the
 * stream's (yellow). Null without the dataset's dates, or when the scale
 * has no length.
 */
export function rtOverlapBar(base: TimeRange | null, range: TimeRange): { base: BarSpan; own: BarSpan } | null {
  if (!base) return null
  const lo = Math.min(base.startMs, range.startMs)
  const hi = Math.max(base.endMs, range.endMs)
  if (!(hi > lo)) return null
  const at = (ms: number) => (ms - lo) / (hi - lo)
  const ownStart = at(range.startMs)
  return {
    base: { start: at(base.startMs), width: at(base.endMs) - at(base.startMs) },
    own: { start: Math.min(ownStart, 1 - MIN_OWN_WIDTH), width: Math.max(MIN_OWN_WIDTH, at(range.endMs) - ownStart) },
  }
}

/** A stream's dates as a short range: years for one longer than three years, else day and month. */
export function rtRangeText(range: TimeRange): string {
  const years = range.endMs - range.startMs > 3 * 365 * 86_400_000
  const opts: Intl.DateTimeFormatOptions = years ? { year: 'numeric', timeZone: 'UTC' } : { day: 'numeric', month: 'short', timeZone: 'UTC' }
  return `${formatDate(range.startMs, opts)} – ${formatDate(range.endMs - 1, opts)}`
}
